// @ts-nocheck
import React, { useState } from 'react';
import { Camera, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../hooks/useAuth';
import { usePhotoContext } from '../contexts/PhotoContext';
import ProfilePhotoCapture from './ProfilePhotoCapture';

interface PhotoRecoveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPhotoUploaded: (photoUrl: string) => void;
}

/**
 * V145.4: NUCLEAR PHOTO RECOVERY SYSTEM
 * Detects users with empty photo_urls and offers recovery options
 */
export default function PhotoRecoveryModal({ isOpen, onClose, onPhotoUploaded }: PhotoRecoveryModalProps) {
  const { user } = useAuth();
  const { updateUserPhoto } = usePhotoContext();
  const [showCamera, setShowCamera] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  if (!isOpen || !user) return null;

  const handleCameraCapture = () => {
    setShowCamera(true);
  };

  const handlePhotoComplete = (photoUrl: string) => {
    console.log('✅ V145.4: Photo recovery successful:', photoUrl);
    updateUserPhoto(user.id, photoUrl);
    onPhotoUploaded(photoUrl);
    setShowCamera(false);
    onClose();
    toast.success('Photo uploaded successfully! Your profile is now complete.');
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // V207.6: Use signed upload instead of unsigned
      const { uploadProfilePhotoSigned } = await import('../lib/cloudinarySignedUpload');

      console.log('📤 V207.6: Starting signed file upload...');
      const { secureUrl, publicId } = await uploadProfilePhotoSigned(file, user.name || undefined);

      // Update database
      const { supabase } = await import('../integrations/supabase/client');
      const { error } = await supabase
        .from('users')
        .update({
          photo_urls: [secureUrl],
          profile_image_public_id: publicId, // V207.6: Store public_id for deletion
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) {
        throw new Error(`Database update failed: ${error.message}`);
      }

      console.log('✅ V265.3: Signed upload successful');

      // V265.3: Use clean URL without cache-buster
      // Cloudinary handles caching correctly
      console.log('🔄 V265.3: Clean Cloudinary URL:', secureUrl.substring(0, 80));

      // V265.3: Preload the new image before updating UI
      const img = new Image();
      img.onload = () => {
        console.log('✅ V265.3: New image preloaded');
        updateUserPhoto(user.id, secureUrl);
        onPhotoUploaded(secureUrl);
        onClose();
        toast.success('Photo uploaded successfully!');
      };
      img.onerror = () => {
        console.warn('⚠️ V265.3: Image preload failed, updating anyway');
        updateUserPhoto(user.id, secureUrl);
        onPhotoUploaded(secureUrl);
        onClose();
        toast.success('Photo uploaded successfully!');
      };
      img.src = secureUrl;

    } catch (error) {
      console.error('❌ V207.6: Signed upload failed:', error);
      toast.error('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  if (showCamera) {
    return (
      <ProfilePhotoCapture
        onComplete={handlePhotoComplete}
        onSkip={() => setShowCamera(false)}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-gray-500" aria-hidden="true" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Camera className="w-8 h-8 text-purple-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Add Your Profile Photo
          </h2>
          <p className="text-gray-600 text-sm">
            Your profile needs a photo to get started. Choose how you'd like to add one:
          </p>
        </div>

        {/* Options */}
        <div className="space-y-3">
          {/* Take Photo */}
          <button
            onClick={handleCameraCapture}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-4 px-6 rounded-xl font-medium transition-colors flex items-center justify-center gap-3"
          >
            <Camera className="w-5 h-5" />
            Take a Selfie
          </button>

          {/* Upload File */}
          <label className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-4 px-6 rounded-xl font-medium transition-colors flex items-center justify-center gap-3 cursor-pointer">
            <Upload className="w-5 h-5" />
            {isUploading ? 'Uploading...' : 'Upload from Device'}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              disabled={isUploading}
              className="hidden"
            />
          </label>
        </div>

        {/* Skip option */}
        <div className="text-center mt-6">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
